import { useState, useRef } from "react";
import { createUserWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { notifyNewMember } from "../../utils/notifications";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../firebase";
import toast, { Toaster } from "react-hot-toast";
import {
  UserPlus, Mail, Phone, User, MapPin, Hash,
  Calendar, Upload, ImageIcon, CreditCard, FileText, Eye, EyeOff, X
} from "lucide-react";

const CLOUD_NAME    = "dan0bapfg";
const UPLOAD_PRESET = "htst_event";
const MAX_SIZE_KB   = 250;

async function uploadToCloudinary(file, folder) {
  const formData = new FormData();
  formData.append("file",          file);
  formData.append("upload_preset", UPLOAD_PRESET);
  formData.append("folder",        `htst_members/${folder}`);
  const res  = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
    method: "POST", body: formData
  });
  if (!res.ok) throw new Error("Upload failed");
  const data = await res.json();
  return data.secure_url;
}

function useFileUpload(label) {
  const [preview, setPreview] = useState(null);
  const [file,    setFile]    = useState(null);
  const [error,   setError]   = useState("");
  const inputRef = useRef(null);

  function handleChange(e) {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { setError("Only image files allowed"); return; }
    if (f.size > MAX_SIZE_KB * 1024) { setError(`Max size is ${MAX_SIZE_KB}KB. This file is ${Math.round(f.size/1024)}KB`); return; }
    setError("");
    setFile(f);
    setPreview(URL.createObjectURL(f));
  }

  function clear() { setFile(null); setPreview(null); setError(""); if (inputRef.current) inputRef.current.value = ""; }

  return { file, preview, error, inputRef, handleChange, clear };
}

export default function AddMember() {
  const [form, setForm] = useState({
    name:"", email:"", phone:"", role:"volunteer",
    address:"", university:"", dob:"", memberId:"",
    aadharNumber:"", panNumber:""
  });
  const [loading,  setLoading]  = useState(false);
  const [step,     setStep]     = useState(1); // 1=basic, 2=documents

  const photo        = useFileUpload("Profile Photo");
  const aadharFront  = useFileUpload("Aadhar Front");
  const aadharBack   = useFileUpload("Aadhar Back");
  const panCard      = useFileUpload("PAN Card");

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  function generateMemberId() {
    const year = new Date().getFullYear();
    const num  = Math.floor(Math.random() * 900) + 100;
    set("memberId", `HTS-${year}-${num}`);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.role) return toast.error("Name, email and role are required");
    setLoading(true);
    try {
      // Create Firebase Auth user
      const tempPassword = `HTS@${Math.random().toString(36).slice(2,8).toUpperCase()}`;
      const cred = await createUserWithEmailAndPassword(auth, form.email, tempPassword);
      const uid  = cred.user.uid;

      toast.loading("Uploading documents...", { id:"upload" });

      // Upload files to Cloudinary
      let photoUrl       = "";
      let aadharFrontUrl = "";
      let aadharBackUrl  = "";
      let panUrl         = "";

      if (photo.file)       photoUrl       = await uploadToCloudinary(photo.file,       `${uid}/photo`);
      if (aadharFront.file) aadharFrontUrl = await uploadToCloudinary(aadharFront.file, `${uid}/aadhar`);
      if (aadharBack.file)  aadharBackUrl  = await uploadToCloudinary(aadharBack.file,  `${uid}/aadhar`);
      if (panCard.file)     panUrl         = await uploadToCloudinary(panCard.file,      `${uid}/pan`);

      toast.dismiss("upload");

      // Save to Firestore
      await setDoc(doc(db, "users", uid), {
        name:          form.name,
        email:         form.email,
        phone:         form.phone,
        role:          form.role,
        address:       form.address,
        university:    form.university,
        dob:           form.dob,
        memberId:      form.memberId || `HTS-${new Date().getFullYear()}-${Math.floor(Math.random()*900)+100}`,
        aadharNumber:  form.aadharNumber,
        panNumber:     form.panNumber,
        photoUrl,
        aadharFrontUrl,
        aadharBackUrl,
        panUrl,
        status:        "active",
        joinDate:      new Date().toISOString().split("T")[0],
        createdAt:     serverTimestamp(),
      });
await notifyNewMember(form.name, form.role);
      // Send password reset email
      await sendPasswordResetEmail(auth, form.email);

      toast.success(`✅ Member added! Password reset email sent to ${form.email}`);

      // Reset form
      setForm({ name:"", email:"", phone:"", role:"volunteer", address:"", university:"", dob:"", memberId:"", aadharNumber:"", panNumber:"" });
      photo.clear(); aadharFront.clear(); aadharBack.clear(); panCard.clear();
      setStep(1);

    } catch(err) {
      console.error(err);
      if (err.code === "auth/email-already-in-use") toast.error("Email already registered");
      else toast.error("Failed: " + err.message);
    } finally { setLoading(false); }
  }

  const ROLES = [
    { v:"volunteer", l:"Volunteer", c:"#a855f7" },
    { v:"member",    l:"Member",    c:"#22c55e" },
    { v:"intern",    l:"Intern",    c:"#ec4899" },
    { v:"admin",     l:"Admin",     c:"#3b82f6" },
  ];

  return (
    <div style={{ maxWidth:680 }}>
      <Toaster position="top-center"/>

      {/* Header */}
      <div style={s.header}>
        <UserPlus size={22} color="#f59e0b"/>
        <div>
          <h2 style={s.title}>Add New Member</h2>
          <p style={s.sub}>Create account and upload KYC documents</p>
        </div>
      </div>

      {/* Step Tabs */}
      <div style={s.steps}>
        {[{ n:1, l:"Basic Info" }, { n:2, l:"Documents & KYC" }].map(({ n, l }) => (
          <button key={n} onClick={() => setStep(n)} style={{
            ...s.stepBtn,
            background: step === n ? "rgba(245,158,11,0.15)" : "transparent",
            color:       step === n ? "#f59e0b" : "#78716c",
            border:      `1px solid ${step === n ? "rgba(245,158,11,0.3)" : "rgba(255,255,255,0.08)"}`,
          }}>
            <span style={{ ...s.stepNum, background: step === n ? "#f59e0b" : "rgba(255,255,255,0.08)", color: step === n ? "#0a0908" : "#78716c" }}>{n}</span>
            {l}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit}>

        {/* ── STEP 1: Basic Info ── */}
        {step === 1 && (
          <>
            {/* Role */}
            <div style={s.card}>
              <div style={s.cardTitle}>Select Role</div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {ROLES.map(({ v, l, c }) => (
                  <button type="button" key={v} onClick={() => set("role", v)} style={{
                    padding:"8px 18px", borderRadius:9, cursor:"pointer", fontFamily:"inherit",
                    border:      `1.5px solid ${form.role===v ? c : "rgba(255,255,255,0.08)"}`,
                    background:  form.role===v ? `${c}18` : "transparent",
                    color:       form.role===v ? c : "#78716c",
                    fontSize:13, fontWeight:700, transition:"all 0.15s",
                  }}>{l}</button>
                ))}
              </div>
            </div>

            {/* Personal Info */}
            <div style={s.card}>
              <div style={s.cardTitle}>Personal Information</div>
              <div style={s.grid2}>
                <Field icon={User}     label="Full Name *"          value={form.name}       onChange={v=>set("name",v)}       placeholder="Rahul Sharma"       required/>
                <Field icon={Mail}     label="Email Address *"       value={form.email}      onChange={v=>set("email",v)}      placeholder="rahul@gmail.com"    required type="email"/>
                <Field icon={Phone}    label="Phone Number"          value={form.phone}      onChange={v=>set("phone",v)}      placeholder="+91 XXXXX XXXXX"/>
                <Field icon={Calendar} label="Date of Birth"         value={form.dob}        onChange={v=>set("dob",v)}        placeholder="YYYY-MM-DD"         type="date"/>
                <Field icon={MapPin}   label="Address"               value={form.address}    onChange={v=>set("address",v)}    placeholder="City, State"/>
                <Field icon={User}     label="University / Org"      value={form.university} onChange={v=>set("university",v)} placeholder="Amity University"/>
                <div style={{ display:"flex", gap:8, alignItems:"flex-end" }}>
                  <div style={{ flex:1 }}>
                    <Field icon={Hash} label="Member ID" value={form.memberId} onChange={v=>set("memberId",v)} placeholder="HTS-2024-XXX"/>
                  </div>
                  <button type="button" onClick={generateMemberId} style={s.autoBtn}>Auto</button>
                </div>
              </div>
            </div>

            <button type="button" onClick={() => setStep(2)} style={{ ...s.nextBtn }}>
              Next: Upload Documents →
            </button>
          </>
        )}

        {/* ── STEP 2: Documents ── */}
        {step === 2 && (
          <>
            {/* Profile Photo */}
            <div style={s.card}>
              <div style={s.cardTitle}><ImageIcon size={15} color="#f59e0b"/> Profile Photo <span style={s.sizeNote}>Max {MAX_SIZE_KB}KB</span></div>
              <UploadBox hook={photo} label="Profile Photo" shape="circle"/>
            </div>

            {/* Aadhar */}
            <div style={s.card}>
              <div style={s.cardTitle}><CreditCard size={15} color="#3b82f6"/> Aadhar Card <span style={{ ...s.sizeNote, color:"#3b82f6" }}>Max {MAX_SIZE_KB}KB each</span></div>
              <Field icon={Hash} label="Aadhar Number" value={form.aadharNumber} onChange={v=>set("aadharNumber",v)} placeholder="XXXX XXXX XXXX"/>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginTop:14 }}>
                <div>
                  <div style={s.uploadLabel}>Front Side</div>
                  <UploadBox hook={aadharFront} label="Aadhar Front"/>
                </div>
                <div>
                  <div style={s.uploadLabel}>Back Side</div>
                  <UploadBox hook={aadharBack} label="Aadhar Back"/>
                </div>
              </div>
            </div>

            {/* PAN Card */}
            <div style={s.card}>
              <div style={s.cardTitle}><FileText size={15} color="#22c55e"/> PAN Card <span style={{ ...s.sizeNote, color:"#22c55e" }}>Max {MAX_SIZE_KB}KB</span></div>
              <Field icon={Hash} label="PAN Number" value={form.panNumber} onChange={v=>set("panNumber",v)} placeholder="ABCDE1234F"/>
              <div style={{ marginTop:14 }}>
                <UploadBox hook={panCard} label="PAN Card"/>
              </div>
            </div>

            {/* Info */}
            <div style={s.infoBox}>
              <div style={{ fontSize:13, color:"#78716c", lineHeight:1.6 }}>
                🔒 All documents are securely stored and only accessible to admins.
                A <strong style={{ color:"#fef3e2" }}>password reset email</strong> will be sent to the member automatically.
              </div>
            </div>

            <div style={{ display:"flex", gap:10 }}>
              <button type="button" onClick={() => setStep(1)} style={s.backBtn}>← Back</button>
              <button type="submit" disabled={loading} style={{ ...s.submitBtn, opacity:loading?0.6:1 }}>
                <UserPlus size={16}/>
                {loading ? "Creating Account..." : "Create Member Account"}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}

// ── Upload Box Component ──
function UploadBox({ hook, label, shape="rect" }) {
  const isCircle = shape === "circle";
  return (
    <div>
      <div
        onClick={() => hook.inputRef.current?.click()}
        style={{
          border:     `2px dashed ${hook.preview ? "#f59e0b" : "rgba(255,255,255,0.1)"}`,
          borderRadius: isCircle ? "50%" : 10,
          cursor:     "pointer",
          overflow:   "hidden",
          transition: "all 0.2s",
          background: hook.preview ? "rgba(245,158,11,0.04)" : "rgba(255,255,255,0.02)",
          width:      isCircle ? 100 : "100%",
          height:     isCircle ? 100 : 120,
          display:    "flex",
          alignItems: "center",
          justifyContent: "center",
          position:   "relative",
          flexShrink: 0,
        }}>
        {hook.preview ? (
          <>
            <img src={hook.preview} alt={label}
              style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
            <button
              type="button"
              onClick={e => { e.stopPropagation(); hook.clear(); }}
              style={{ position:"absolute", top:4, right:4, background:"rgba(0,0,0,0.7)", border:"none", borderRadius:"50%", width:22, height:22, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:"#fff" }}>
              <X size={12}/>
            </button>
          </>
        ) : (
          <div style={{ textAlign:"center", padding:8 }}>
            <Upload size={20} color="#52504d" style={{ marginBottom:4 }}/>
            <div style={{ fontSize:11, color:"#52504d" }}>Click to upload</div>
          </div>
        )}
      </div>
      {hook.error && <div style={{ fontSize:11, color:"#ef4444", marginTop:5 }}>⚠️ {hook.error}</div>}
      <input ref={hook.inputRef} type="file" accept="image/*" onChange={hook.handleChange} style={{ display:"none" }}/>
    </div>
  );
}

// ── Field Component ──
function Field({ icon:Icon, label, value, onChange, placeholder, type="text", required }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#78716c", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:5 }}>{label}</label>
      <div style={{ position:"relative" }}>
        <Icon size={13} color="#52504d" style={{ position:"absolute", left:11, top:"50%", transform:"translateY(-50%)" }}/>
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          style={{ width:"100%", padding:"10px 12px 10px 30px", background:"#0a0908", border:"1px solid rgba(255,255,255,0.08)", borderRadius:9, color:"#fef3e2", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}
        />
      </div>
    </div>
  );
}

const s = {
  header:     { display:"flex", alignItems:"center", gap:14, marginBottom:20, background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18 },
  title:      { fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:800, color:"#fef3e2", margin:"0 0 4px" },
  sub:        { fontSize:13, color:"#78716c", margin:0 },
  steps:      { display:"flex", gap:10, marginBottom:16 },
  stepBtn:    { display:"flex", alignItems:"center", gap:8, padding:"10px 18px", borderRadius:10, cursor:"pointer", fontSize:13, fontWeight:700, fontFamily:"inherit", transition:"all 0.2s", flex:1, justifyContent:"center" },
  stepNum:    { width:22, height:22, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, flexShrink:0 },
  card:       { background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20, marginBottom:14 },
  cardTitle:  { fontSize:13, fontWeight:700, color:"#fef3e2", marginBottom:14, display:"flex", alignItems:"center", gap:7 },
  sizeNote:   { fontSize:10, color:"#52504d", fontWeight:600, marginLeft:"auto" },
  grid2:      { display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 },
  autoBtn:    { padding:"10px 14px", background:"rgba(245,158,11,0.1)", border:"1px solid rgba(245,158,11,0.25)", borderRadius:9, color:"#f59e0b", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", flexShrink:0, marginBottom:0, height:40 },
  uploadLabel:{ fontSize:11, fontWeight:700, color:"#78716c", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:8 },
  infoBox:    { background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:12, padding:14, marginBottom:16 },
  nextBtn:    { width:"100%", padding:"13px", background:"#f59e0b", color:"#0a0908", border:"none", borderRadius:12, fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"inherit", marginBottom:14 },
  backBtn:    { padding:"13px 20px", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:12, color:"#a8a29e", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"inherit" },
  submitBtn:  { flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"13px", background:"#f59e0b", color:"#0a0908", border:"none", borderRadius:12, fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"inherit" },
};